import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface WinningCardProps {
  winAmount: number;
  winPoints: number;
  animatedPoints: number;
  couponCode: string;
  productName?: string;
  productPhoto?: string;
  timestamp?: Date;
  onClose: () => void;
}

const WinningCard: React.FC<WinningCardProps> = ({
  winAmount,
  winPoints,
  animatedPoints,
  productName,
  productPhoto,
  timestamp,
  onClose
}) => {
  const { t } = useTranslation();
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formattedDate = useMemo(() => {
    if (!timestamp) return '';
    return timestamp.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }, [timestamp]);

  // Gold Dust Particles
  const particles = useMemo(() => {
    return [...Array(40)].map((_, i) => ({
      left: `${Math.random() * 100}%`,
      bottom: `-${Math.random() * 20}%`,
      width: `${2 + Math.random() * 6}px`,
      height: `${2 + Math.random() * 6}px`,
      bg: ['#FFD700', '#F0E68C', '#FFFACD', '#DAA520'][Math.floor(Math.random() * 4)],
      duration: 3 + Math.random() * 4,
      delay: Math.random() * 5
    }));
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      // DEEP LUXURY BACKGROUND
      background: 'radial-gradient(circle at 50% 30%, #2a221b 0%, #000000 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: isMobile ? '16px' : '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      
      <style>
        {`
          @keyframes floatUp {
            0% { transform: translateY(0) scale(0.5); opacity: 0; }
            20% { opacity: 0.8; }
            100% { transform: translateY(-120vh) scale(1.5); opacity: 0; }
          }
          @keyframes shine {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          @keyframes cardFloat {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
            100% { transform: translateY(0px); }
          }
        `}
      </style>

      {/* Floating Gold Particles */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: p.left,
            bottom: '-20px',
            width: p.width,
            height: p.height,
            backgroundColor: p.bg,
            borderRadius: '50%',
            animation: `floatUp ${p.duration}s linear infinite`,
            animationDelay: `${p.delay}s`,
            boxShadow: '0 0 10px rgba(255, 215, 0, 0.6)',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        />
      ))}
      
      {/* Main Container */}
      <div style={{
        maxWidth: isMobile ? '90%' : '500px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        position: 'relative',
        zIndex: 100
      }}>
        
        {/* Luxury Heading */}
        <h1 style={{
          fontSize: isMobile ? '32px' : '48px',
          fontFamily: '"Playfair Display", "Times New Roman", serif',
          fontWeight: 700,
          margin: '0 0 8px 0',
          letterSpacing: '1px',
          background: 'linear-gradient(to right, #BF953F, #FCF6BA, #B38728, #FBF5B7, #AA771C)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
        }}>
          Congratulations!
        </h1>
        
        <p style={{
          fontSize: isMobile ? '14px' : '16px',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '0 0 30px 0',
          fontWeight: 300,
          letterSpacing: '1px',
          textTransform: 'uppercase'
        }}>
          You have earned rewards
        </p>

        {/* --- THE MASTERPIECE CARD --- */}
        <div style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '450px',
          background: 'linear-gradient(135deg, #a67c00 0%, #bf953f 25%, #fcf6ba 50%, #bf953f 75%, #a67c00 100%)',
          borderRadius: '20px',
          padding: '2px',
          position: 'relative',
          marginBottom: isMobile ? '24px' : '32px',
          animation: 'cardFloat 6s ease-in-out infinite',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.9)'
        }}>
          {/* Inner Card Body */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.0))',
            borderRadius: '18px',
            padding: isMobile ? '20px' : '28px',
            width: '100%',
            minHeight: '200px', // Ensure height balance
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            
            {/* Texture & Shine */}
            <div style={{
               position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
               background: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 2px, transparent 2px, transparent 4px)',
               pointerEvents: 'none'
            }}></div>
            <div style={{
              position: 'absolute', top: 0, left: '-100%', width: '200%', height: '100%',
              background: 'linear-gradient(to right, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
              transform: 'skewX(-20deg)',
              animation: 'shine 4s infinite linear',
              pointerEvents: 'none'
            }}></div>

            {/* CARD CONTENT ROW */}
            <div style={{ 
              position: 'relative', 
              zIndex: 10, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%',
              gap: '0px'
            }}>
              
              {/* Cash Section */}
              <div style={{ 
                flex: 1, 
                textAlign: 'center', 
                padding: '0 10px',
                borderRight: winPoints > 0 ? '1px solid rgba(42, 26, 0, 0.15)' : 'none'
              }}>
                <h2 style={{
                  fontSize: isMobile ? '32px' : '42px', // Equal Big Size
                  fontWeight: 800,
                  color: '#2a1a00',
                  margin: '0 0 4px 0',
                  lineHeight: 1,
                  fontFamily: "'Playfair Display', serif",
                  textShadow: '0 1px 0 rgba(255,255,255,0.4)'
                }}>
                  ₹{winAmount}
                </h2>
                <p style={{
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#4b3621',
                  margin: 0,
                  opacity: 0.8
                }}>
                  Cash Reward
                </p>
              </div>

              {/* Points Section (Only if points exist) */}
              {winPoints > 0 && (
                <div style={{ 
                  flex: 1, 
                  textAlign: 'center', 
                  padding: '0 10px',
                  borderLeft: '1px solid rgba(255, 255, 255, 0.4)' // Highlight for divider
                }}>
                   <h2 style={{
                    fontSize: isMobile ? '32px' : '42px', // EXACT SAME SIZE AS CASH
                    fontWeight: 800,
                    color: '#2a1a00',
                    margin: '0 0 4px 0',
                    lineHeight: 1,
                    fontFamily: "'Playfair Display', serif",
                    textShadow: '0 1px 0 rgba(255,255,255,0.4)'
                  }}>
                    ₽{animatedPoints}
                  </h2>
                  <p style={{
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: '#4b3621',
                    margin: 0,
                    opacity: 0.8
                  }}>
                    Reward Points
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* --- END CARD --- */}

        {/* Product Info */}
        {(productPhoto || productName || timestamp) && (
          <div style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '420px',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(10px)',
            borderRadius: '14px',
            padding: '12px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            {productPhoto && (
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '10px',
                overflow: 'hidden',
                background: '#fff',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <img 
                  src={productPhoto} 
                  alt={productName || 'Product'} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            )}

            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              {productName && (
                <h4 style={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#ffffff',
                  margin: '0 0 4px 0',
                  letterSpacing: '0.3px'
                }}>
                  {productName}
                </h4>
              )}
              {timestamp && (
                <p style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.5)',
                  margin: 0
                }}>
                  {formattedDate}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Action Button */}
        <div style={{ width: '100%', maxWidth: isMobile ? '100%' : '420px' }}>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: '16px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              border: 'none',
              borderRadius: '12px',
              background: 'linear-gradient(to bottom, #d4af37, #aa8826)',
              color: '#1a1a1a',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(212, 175, 55, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 25px rgba(212, 175, 55, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(212, 175, 55, 0.3)';
            }}
          >
            {t('scanner.scan_another', 'Scan Next')}
          </button>
          
          <p style={{
            fontSize: '11px',
            color: 'rgba(255, 255, 255, 0.3)',
            marginTop: '16px',
            marginBottom: 0
          }}>
            {t('scanner.instantly_credited', 'Rewards credited instantly to your account')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WinningCard;